import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useAppointments,
  useCheckInAppointment,
  useCancelAppointment,
  useBulkDeleteAppointments,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { AppointmentDetailsModal } from '../../components/ui/AppointmentDetailsModal';
import { AppointmentPrintModal } from '../../components/ui/AppointmentPrintModal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { formatDateWithWeekday, formatTimeRange } from '../../utils/dateFormat';
import {
  Calendar,
  Plus,
  Loader2,
  AlertCircle,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Search,
  X,
  Trash2,
  Square,
  SquareCheck,
  Printer,
  LogIn,
  Ban,
  Pencil,
} from 'lucide-react';

const statusColors: Record<string, string> = {
  SCHEDULED: 'bg-secondary/10 text-secondary',
  CHECKED_IN: 'bg-primary/10 text-primary',
  IN_PROGRESS: 'bg-warning/10 text-warning',
  COMPLETED: 'bg-success/10 text-success',
  CANCELLED: 'bg-danger/10 text-danger',
  NO_SHOW: 'bg-text-muted/10 text-text-muted',
};

export function Appointments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const { data, isLoading, error } = useAppointments(page, limit, debouncedSearch || undefined);
  const checkInAppointment = useCheckInAppointment();
  const cancelAppointment = useCancelAppointment();
  const bulkDeleteAppointments = useBulkDeleteAppointments();

  const appointments = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Clear selection whenever the visible page changes.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, search]);

  const getPatientName = (appointment: any) => {
    const patient = appointment?.patient;
    if (patient) {
      return joinFullName(patient) || patient.registrationNumber || 'Unknown';
    }
    return appointment?.patientName || 'Walk-in patient';
  };
  const getDoctorName = (doctor: any) => doctor?.name || 'Unknown';

  const handlePatientClick = (appointment: any) => {
    setSelectedAppointment(appointment);
  };

  const handleCheckIn = (id: string) => {
    checkInAppointment.mutate(id);
  };

  const handleCancel = (id: string) => {
    cancelAppointment.mutate(id);
  };

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  const allSelected =
    appointments.length > 0 && appointments.every((a: any) => selectedIds.includes(a.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !appointments.some((a: any) => a.id === id)));
    } else {
      const newIds = appointments.map((a: any) => a.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    const ids = selectedIds;
    await bulkDeleteAppointments.mutateAsync(ids);
    setSelectedIds([]);
    setBulkConfirmOpen(false);
    if (page > 1 && ids.length >= appointments.length) {
      setPage((p) => Math.max(1, p - 1));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Appointments</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-surface border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-sm ${view === 'list' ? 'bg-primary text-white' : 'text-text-muted hover:text-text'}`}
            >
              List
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-3 py-1.5 text-sm ${view === 'calendar' ? 'bg-primary text-white' : 'text-text-muted hover:text-text'}`}
            >
              Calendar
            </button>
          </div>
          <button
            onClick={() => setShowPrintModal(true)}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Printer size={16} />
            Print List
          </button>
          <button
            onClick={() => navigate('/appointments/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Book Appointment
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search appointments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {search && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load appointments</span>
        </div>
      )}

      {/* Bulk Actions */}
      {someSelected && (
        <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-3">
          <span className="text-sm text-text-muted mr-2">{selectedIds.length} selected</span>
          <button
            onClick={() => setBulkConfirmOpen(true)}
            disabled={bulkDeleteAppointments.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {bulkDeleteAppointments.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Trash2 size={12} />
            )}
            Delete
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="text-text-muted hover:text-text transition-colors"
                      aria-label="Select all appointments"
                    >
                      {allSelected ? (
                        <SquareCheck size={18} className="text-primary" />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Serial</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Doctor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {appointments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>No appointments match your search.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No appointments scheduled'
                      )}
                    </td>
                  </tr>
                ) : (
                  appointments.map((appointment: any) => (
                    <tr key={appointment.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelectOne(appointment.id)}
                          className="text-text-muted hover:text-text transition-colors"
                          aria-label="Select appointment"
                        >
                          {selectedIds.includes(appointment.id) ? (
                            <SquareCheck size={18} className="text-primary" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User size={14} className="text-primary" />
                          </div>
                          <button
                            onClick={() => handlePatientClick(appointment)}
                            className="text-sm text-text hover:text-primary hover:underline transition-colors text-left"
                            title="View patient details"
                          >
                            {getPatientName(appointment)}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {appointment.serialNumber ? (
                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-semibold text-text">
                              #{appointment.serialNumber}
                            </span>
                            {appointment.session && (
                              <span className="text-[0.6875rem] text-text-muted">{appointment.session}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text">
                          <Calendar size={14} className="text-text-muted" />
                          {formatDateWithWeekday(appointment.date)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text">
                          <Clock size={14} className="text-text-muted" />
                          {formatTimeRange(appointment.time, appointment.endTime) || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                         <span className="text-sm text-text">{getDoctorName(appointment.doctor)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[appointment.status] || 'bg-text-muted/10 text-text-muted'}`}>
                          {appointment.status === 'COMPLETED' && <CheckCircle size={12} />}
                          {appointment.status === 'CANCELLED' && <XCircle size={12} />}
                          {appointment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {appointment.status === 'SCHEDULED' && (
                            <button
                              onClick={() => handleCheckIn(appointment.id)}
                              disabled={checkInAppointment.isPending}
                              title="Check In"
                              aria-label="Check in appointment"
                              className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                            >
                              {checkInAppointment.isPending && checkInAppointment.variables === appointment.id ? (
                                <Loader2 size={15} className="animate-spin" />
                              ) : (
                                <LogIn size={15} />
                              )}
                            </button>
                          )}
                          {appointment.status === 'SCHEDULED' && (
                            <button
                              onClick={() => handleCancel(appointment.id)}
                              disabled={cancelAppointment.isPending}
                              title="Cancel"
                              aria-label="Cancel appointment"
                              className="p-2 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 transition-colors disabled:opacity-50"
                            >
                              {cancelAppointment.isPending && cancelAppointment.variables === appointment.id ? (
                                <Loader2 size={15} className="animate-spin" />
                              ) : (
                                <Ban size={15} />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/appointments/${appointment.id}/edit`)}
                            title="Edit"
                            aria-label="Edit appointment"
                            className="p-2 rounded-lg bg-surface border border-border text-text hover:bg-background hover:text-primary transition-colors"
                          >
                            <Pencil size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="appointments"
          />
        </div>
      )}

      {selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
        />
      )}

      {showPrintModal && (
        <AppointmentPrintModal onClose={() => setShowPrintModal(false)} />
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} appointment${selectedIds.length === 1 ? '' : 's'}?`}
        description="This will permanently remove the selected appointments along with their reminders. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length} appointment${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleteAppointments.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
